import { formatMoney } from '../lib/money';
import { keepChoicesDropdownVisible } from '../modules/choices-keep-visible';
import { buildVariantMap, variantIdFor } from '../modules/variant-map';

interface ShopifyVariant {
  id: number;
  title: string;
  options: string[];
  option1: string;
  option2: string;
  option3: string;
  available: boolean;
  price: number;
  featured_media?: { id: string | number } | null;
}

interface CaseTypeOption {
  label: string;
  value: string;
}

interface MediaGalleryElement extends HTMLElement {
  setActiveMedia(mediaId: string, prepend?: boolean): void;
}

interface PickupAvailabilityElement extends HTMLElement {
  fetchAvailability(variantId: number): void;
}

interface ShareButtonElement extends HTMLElement {
  updateUrl(url: string): void;
}

// ---------------------------------------------------------------------------
// VariantSelects (base)
// ---------------------------------------------------------------------------

class VariantSelects extends HTMLElement {
  options: string[] = [];
  currentVariant: ShopifyVariant | undefined;
  variantData: ShopifyVariant[] | undefined;
  variantChangeEvent: Event;

  constructor() {
    super();
    this.variantChangeEvent = new Event('variant-changed', { bubbles: true });
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback(): void {
    // A device-locked collection link can land here with `?variant=` pointing at a MagSafe
    // Ring combo that's out of stock. Liquid pre-checks that value (disabled) by default, and
    // everything else in this class only reacts to a 'change' event — so nothing corrects the
    // initial selection until the customer clicks something themselves. Simulate that click
    // once on connect so Add to Cart, price, and media reflect an in-stock choice right away.
    const magsafeFieldset = this.querySelector<HTMLElement>('[data-option-name="magsafe-ring"]');
    const checkedRadio = magsafeFieldset?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
    const firstAvailable = magsafeFieldset?.querySelector<HTMLInputElement>('input[type="radio"]:not([disabled])');
    if (checkedRadio?.disabled && firstAvailable) {
      firstAvailable.checked = true;
      firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  onVariantChange(): void {
    // When nested inside variant-handler, defer all variant logic to the parent handler.
    if (this.closest('variant-handler')) {
      this.dispatchEvent(this.variantChangeEvent);
      return;
    }

    this.dispatchEvent(this.variantChangeEvent);
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions(): void {
    this.options = Array.from(
      this.querySelectorAll<HTMLSelectElement>('select'),
      (select) => select.value
    );
  }

  updateMasterId(): void {
    this.currentVariant = this.getVariantData().find((variant) =>
      !variant.options.map((option, index) => this.options[index] === option).includes(false)
    );

    if (!this.currentVariant) {
      this.currentVariant = this.getVariantData().find((variant) =>
        this.options[0] === variant.options[0] ? variant.options : undefined
      );
    }
  }

  updateMedia(): void {
    if (!this.currentVariant?.featured_media) return;

    const mediaGallery = document.getElementById(
      `MediaGallery-${this.dataset.section}`
    ) as MediaGalleryElement | null;
    if (!mediaGallery) return;

    mediaGallery.setActiveMedia(
      `${this.dataset.section}-${this.currentVariant.featured_media.id}`,
      true
    );

    const thumbnailSlider = mediaGallery.querySelector<HTMLElement>('.thumbnail-slider');
    if (thumbnailSlider) {
      const firstImg = thumbnailSlider.querySelector<HTMLImageElement>('li:first-of-type img');
      const sourceImg = mediaGallery.querySelector<HTMLImageElement>(
        '.product__media-list li:first-of-type .product__media img'
      );
      if (firstImg && sourceImg) {
        firstImg.srcset = '';
        firstImg.src = sourceImg.src;
      }
    }

    const modalContent = document.querySelector(
      `#ProductModal-${this.dataset.section} .product-media-modal__content`
    );
    if (!modalContent) return;
    const modalFirstImg = modalContent.querySelector<HTMLImageElement>('img:first-of-type');
    if (modalFirstImg) {
      const sourceImg = mediaGallery.querySelector<HTMLImageElement>(
        '.product__media-list li:first-of-type .product__media img'
      );
      if (sourceImg) {
        modalFirstImg.srcset = '';
        modalFirstImg.src = sourceImg.src;
      }
    }
  }

  updateURL(): void {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl(): void {
    if (!this.currentVariant) return;
    const shareButton = document.getElementById(`Share-${this.dataset.section}`) as ShareButtonElement | null;
    if (!shareButton) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput(): void {
    if (!this.currentVariant) return;
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment, .product-form-addon, .case_type--container`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector<HTMLInputElement>('input[name="id"]');
      if (input) {
        input.value = String(this.currentVariant!.id);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    // Notify Shopify apps (e.g. Klaviyo BIS) that follow the Dawn variant:change convention
    document.dispatchEvent(
      new CustomEvent('variant:change', { detail: { variant: this.currentVariant } })
    );
  }

  updatePickupAvailability(): void {
    const pickUpAvailability = document.querySelector<PickupAvailabilityElement>('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant?.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage(): void {
    // Placeholder — product-form handles error messages
  }

  renderProductInfo(): void {
    if (!this.currentVariant) return;

    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}`)
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.getElementById(id);

        if (source) {
          // This innerHTML swap also re-renders the Magsafe Ring price diff span
          // (`.magsafe-price-diff`) — the server returns the correct delta for the
          // fetched variant's device. Do not narrow this swap without updating the
          // Magsafe price diff separately.
          document.querySelectorAll(`#${id}`).forEach((dest) => {
            dest.innerHTML = source.innerHTML;
          });
        }

        // The Liquid price template uses price_max for Phone Cases regardless of the
        // selected variant. Override the rendered price with the actual selected variant
        // price so that selecting "With" vs "Without" Magsafe Ring shows the correct amount.
        // Reset originalVariantPrice to null so that the next fake-variant interaction
        // re-captures the new base price instead of compounding on the old device's price.
        if (this.currentVariant) {
          originalVariantPrice = null;
        }

        const priceContainer = source?.querySelector('.price__container');
        document.querySelectorAll(
          `.product__info-wrapper .price__container, .section-product-buy-banner .price__container, .section-product-top-mobile .price__container`
        ).forEach((p) => {
          p.classList.remove('visibility-hidden');
          if (p.parentElement?.classList.contains('upsell-price')) return;
          if (priceContainer) p.innerHTML = priceContainer.innerHTML;
        });

        const fakeVariantSelector = '.false-variant-container';
        const fakeSources = html.querySelectorAll(fakeVariantSelector);
        document.querySelectorAll(fakeVariantSelector).forEach((dest) => {
          const matching = Array.from(fakeSources).find((s) => s.id === dest.id);
          if (matching) {
            dest.innerHTML = matching.innerHTML;
            dest.classList.remove('--hidden');
            setupFakeVariantPricing();
          } else {
            dest.classList.add('--hidden');
          }
        });

        const caseTypeSelector = document.querySelector<HTMLElement>('.case_type--container:not([data-option-name="magsafe-ring"])');
        if (caseTypeSelector) {
          caseTypeSelector.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach((input) => {
            let caseTypeValue = input.value;
            // Compare option values directly instead of the full title string so that
            // products with 3+ options (e.g. Device / CaseType / MagsafeRing) still match.
            let isVariant = this.getVariantData().find(
              (v) => v.option1 === this.currentVariant!.option1 && v.option2 === caseTypeValue
            );
            if (!isVariant) {
              if (caseTypeValue === 'Impact (Mat)') caseTypeValue = 'Impact (Matte)';
              else if (caseTypeValue === 'Impact (Lustré)') caseTypeValue = 'Impact (Gloss)';
              isVariant = this.getVariantData().find(
                (v) => v.option1 === this.currentVariant!.option1 && v.option2 === caseTypeValue
              );
            }
            input.disabled = !isVariant;
          });

          const checkedDisabled = caseTypeSelector.querySelector<HTMLInputElement>('input[type="radio"]:checked');
          const firstEnabled = caseTypeSelector.querySelector<HTMLInputElement>('input[type="radio"]:not([disabled])');
          if (checkedDisabled?.disabled && firstEnabled) {
            firstEnabled.checked = true;
          }
        }

        // Update Magsafe Ring option availability and fieldset visibility.
        // The row hides only when the current DEVICE (ignoring Case Type/Finish) has
        // a single MagSafe Ring value that actually exists as a variant — not when a
        // value merely happens to be out of stock right now (it stays visible,
        // disabled, so the customer can see the choice exists). Mirrors Liquid's
        // magsafe_available_count in product-info.liquid — keep both in sync.
        const magsafeFieldset = document.querySelector<HTMLElement>('[data-option-name="magsafe-ring"]');
        if (magsafeFieldset) {
          const magsafePos = parseInt(magsafeFieldset.dataset.optionPosition ?? '0');
          const magsafeRadios = magsafeFieldset.querySelectorAll<HTMLInputElement>('input[type="radio"]');
          let existingValueCount = 0;
          const magsafeKey = `option${magsafePos}` as 'option1' | 'option2' | 'option3';
          const currentDevice = this.currentVariant?.option1;

          magsafeRadios.forEach((radio) => {
            // Existence (drives row visibility below): scoped to the selected DEVICE
            // only, ignoring Case Type/Finish — a value that ships under a different
            // Finish for the same device still counts.
            const existsForDevice = this.getVariantData().some(
              (variant) => (!currentDevice || variant.option1 === currentDevice) && variant[magsafeKey] === radio.value,
            );

            // Availability (drives this radio's disabled state): scoped to the exact
            // current combination, since picking this value must resolve to a real,
            // in-stock variant for the Case Type/Finish currently selected.
            const matchesCurrentCombo = (variant: ShopifyVariant): boolean => {
              // Shopify supports at most 3 options per product, so i <= 3 is exhaustive.
              for (let i = 1; i <= 3; i++) {
                if (i === magsafePos) continue;
                const currentVal = this.currentVariant![`option${i}` as 'option1' | 'option2' | 'option3'];
                if (currentVal && variant[`option${i}` as 'option1' | 'option2' | 'option3'] !== currentVal) return false;
              }
              return variant[magsafeKey] === radio.value;
            };
            const isAvailable = this.getVariantData().some((variant) => variant.available && matchesCurrentCombo(variant));

            radio.disabled = !isAvailable;
            if (existsForDevice) existingValueCount++;
          });

          magsafeFieldset.classList.toggle('hidden', existingValueCount <= 1);

          // If the currently checked option is no longer available, select the first available one.
          const checkedRadio = magsafeFieldset.querySelector<HTMLInputElement>('input[type="radio"]:checked');
          if (checkedRadio?.disabled) {
            const firstAvailable = magsafeFieldset.querySelector<HTMLInputElement>('input[type="radio"]:not([disabled])');
            if (firstAvailable) {
              firstAvailable.checked = true;
              this.updateOptions();
              this.updateMasterId();
              if (this.currentVariant) this.updateVariantInput();
            }
          }
        }

        this.toggleAddButton(!this.currentVariant!.available, window.variantStrings?.soldOut ?? '');
      });
  }

  toggleAddButton(disable = true, text: string, modifyClass = true): void {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );
    if (!productForms.length) return;

    productForms.forEach(() => {
      const addButtons = document.querySelectorAll('[data-add-to-cart-button]');
      const addButtonTexts = document.querySelectorAll('[data-add-to-cart-button] > span');
      if (!addButtons.length) return;

      if (disable) {
        addButtons.forEach((btn) => btn.setAttribute('disabled', 'disabled'));
        if (text) addButtonTexts.forEach((btn) => (btn.textContent = text));
      } else {
        addButtons.forEach((btn) => btn.removeAttribute('disabled'));
        addButtonTexts.forEach((btn) => (btn.textContent = window.variantStrings?.addToCart ?? ''));
      }
    });

    if (!modifyClass) return;
  }

  setUnavailable(): void {
    const addButtons = document.querySelectorAll('[data-add-to-cart-button]');
    const addButtonTexts = document.querySelectorAll('[data-add-to-cart-button] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);

    if (!addButtons.length) return;
    addButtonTexts.forEach((btn) => (btn.textContent = window.variantStrings?.unavailable ?? ''));
    addButtons.forEach((btn) => (btn as HTMLButtonElement).disabled = true);
    if (price) price.classList.add('visibility-hidden');
  }

  getVariantData(): ShopifyVariant[] {
    this.variantData ??= JSON.parse(
      this.querySelector<HTMLScriptElement>('[type="application/json"]')?.textContent ?? '[]'
    );
    return this.variantData!;
  }
}

customElements.define('variant-selects', VariantSelects);

// ---------------------------------------------------------------------------
// Fake variant pricing
// ---------------------------------------------------------------------------

let originalVariantPrice: number | null = null;

function setupFakeVariantPricing(): void {
  document.querySelectorAll<HTMLInputElement>(
    '.false-variant-container input[type="radio"], .false-variant-container input[type="checkbox"]'
  ).forEach((input) => input.addEventListener('change', handleFakeVariantPriceChange));

  document.querySelectorAll<HTMLInputElement>(
    '.variant-addon-container input[data-variant-addon-option="true"]'
  ).forEach((input) => input.addEventListener('change', handleFakeVariantPriceChange));
}

function handleFakeVariantPriceChange(event: Event): void {
  updateProductPricesWithFakeVariant(event.target as HTMLInputElement);
}

function updateProductPricesWithFakeVariant(_selectedInput: HTMLInputElement): void {
  if (originalVariantPrice === null) {
    const variantSelects = document.querySelector<VariantSelects & { currentVariant?: ShopifyVariant; getVariantData?: () => ShopifyVariant[] }>('variant-selects');
    let currentVariantPrice = 0;

    if ((variantSelects as any)?.currentVariant?.price) {
      currentVariantPrice = (variantSelects as any).currentVariant.price;
    } else if (variantSelects) {
      const variantData: ShopifyVariant[] = typeof (variantSelects as any).getVariantData === 'function'
        ? (variantSelects as any).getVariantData()
        : [];
      const selectedOptions = Array.from(
        variantSelects.querySelectorAll<HTMLSelectElement>('select'),
        (s) => s.value
      );
      const currentVariant = variantData.find(
        (v) => !v.options.map((o, i) => selectedOptions[i] === o).includes(false)
      );
      if (currentVariant?.price) currentVariantPrice = currentVariant.price;
    }

    if (currentVariantPrice === 0) {
      const priceEl = document.querySelector<HTMLElement>('.price__container .price-item--regular');
      if (priceEl) {
        const priceText = (priceEl.textContent ?? '').replace(/[^0-9.,]/g, '');
        currentVariantPrice = Math.round(parseFloat(priceText) * 100);
      }
    }

    originalVariantPrice = currentVariantPrice;
  }

  const totalPrice = originalVariantPrice + calculateAllSelectedFakeVariantPrices();
  updateAllPriceDisplays(totalPrice);
}

function updateAllPriceDisplays(newPrice: number): void {
  const formattedPrice = formatMoney(newPrice);

  document.querySelectorAll<HTMLElement>('.price__container .price-item--regular').forEach((el) => {
    if (!el.closest('.upsell-product-container, .upsell-product, [class*="upsell"]')) {
      el.textContent = formattedPrice;
    }
  });

  const buyBannerPrice = document.querySelector<HTMLElement>(
    '.section-product-buy-banner .price__container .price-item--regular'
  );
  if (buyBannerPrice && !buyBannerPrice.closest('.upsell-product-container, .upsell-product, [class*="upsell"]')) {
    buyBannerPrice.textContent = formattedPrice;
  }
}

function calculateAllSelectedFakeVariantPrices(): number {
  let total = 0;

  document.querySelectorAll<HTMLInputElement>(
    '.false-variant-container input[data-bundle-option="true"]:checked'
  ).forEach((input) => {
    if (input.dataset.price) total += parseInt(input.dataset.price);
  });

  document.querySelectorAll<HTMLInputElement>(
    '.variant-addon-container input[data-variant-addon-option="true"]:checked'
  ).forEach((input) => {
    if (input.dataset.price && input.value !== 'null') total += parseInt(input.dataset.price);
  });

  return total;
}


document.addEventListener('DOMContentLoaded', setupFakeVariantPricing);
setupFakeVariantPricing();

// ---------------------------------------------------------------------------
// Related products overflow (colors past Shopify's 20-object all_products limit)
// ---------------------------------------------------------------------------

interface RelatedProductJson {
  title: string;
  url: string;
  handle: string;
  featured_image: string | null;
  variants: Array<{ id: number; option1: string | null; available: boolean; featured_image: { src: string } | null }>;
}

/**
 * Fetches and appends the colors that couldn't be rendered server-side because
 * they sit past Shopify's 20-object `all_products[handle]` limit (see the
 * comment in product-colors.liquid). Reads the handles + matching context off
 * `.related-products__list[data-overflow-refs]`, so it's a no-op on any list
 * that stayed within the limit.
 */
function hydrateRelatedProductsOverflow(): void {
  document.querySelectorAll<HTMLUListElement>('.related-products__list[data-overflow-refs]').forEach((list) => {
    if (list.dataset.overflowHydrated) return;
    list.dataset.overflowHydrated = 'true';

    let handles: string[] = [];
    try {
      handles = JSON.parse(list.dataset.overflowRefs ?? '[]');
    } catch {
      return;
    }
    if (!handles.length) return;

    const context = {
      currentHandle: list.dataset.currentHandle ?? '',
      productType: list.dataset.productType ?? '',
      currentOption1: list.dataset.currentOption1 ?? '',
    };

    handles.forEach((handle) => {
      fetch(`/products/${handle}.js`)
        .then((res) => (res.ok ? (res.json() as Promise<RelatedProductJson>) : null))
        .then((product) => {
          if (!product) return;
          const li = buildRelatedProductItem(product, context);
          if (li) list.appendChild(li);
        })
        .catch(() => {
          // One missing colorway shouldn't break the rest of the swatch list.
        });
    });
  });
}

/** Mirrors the variant/image resolution in product-colors.liquid's for-loop. */
function buildRelatedProductItem(
  product: RelatedProductJson,
  context: { currentHandle: string; productType: string; currentOption1: string }
): HTMLLIElement | null {
  let variantId: number | null = product.variants.find((v) => v.available)?.id ?? null;
  let image = product.featured_image;

  if (context.productType !== 'AirGrip' && context.currentOption1) {
    const match = product.variants.find((v) => v.option1 === context.currentOption1);
    if (match) {
      variantId = match.id;
      if (match.featured_image?.src) image = match.featured_image.src;
    }
  }
  if (!image) return null;

  const href = context.productType !== 'AirGrip'
    ? (variantId ? `${product.url}?variant=${variantId}` : null)
    : product.url;
  if (!href) return null;

  const li = document.createElement('li');
  li.className = 'related-products__container';

  const a = document.createElement('a');
  a.className = `related-products__link${product.handle === context.currentHandle ? ' selected' : ''}`;
  a.href = href;
  a.dataset.variantMap = JSON.stringify(buildVariantMap(product.variants));

  const imageContainer = document.createElement('div');
  imageContainer.className = 'related-products__image-container';

  const img = document.createElement('img');
  img.src = `${image}${image.includes('?') ? '&' : '?'}width=240`;
  img.alt = `${product.title} -`;
  img.className = 'related-products__image';
  img.loading = 'lazy';

  imageContainer.appendChild(img);
  a.appendChild(imageContainer);
  li.appendChild(a);
  return li;
}

document.addEventListener('DOMContentLoaded', hydrateRelatedProductsOverflow);
hydrateRelatedProductsOverflow();

// ---------------------------------------------------------------------------
// VariantRadios
// ---------------------------------------------------------------------------

class VariantRadios extends VariantSelects {
  onVariantChange(): void {
    // When nested inside variant-handler, this element only knows its own radio values —
    // not the full option set. The parent handler reads all inputs together and manages
    // the complete variant state. Dispatch the event so the parent is notified, then bail
    // out so we don't call setUnavailable() with an incomplete options array.
    if (this.closest('variant-handler')) {
      this.dispatchEvent(this.variantChangeEvent);
      return;
    }

    this.dispatchEvent(this.variantChangeEvent);
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();

      if (document.querySelector('.--impact')) {
        this.updateInspiration();
      }
    }
  }

  updateOptions(): void {
    const fieldsets = Array.from(this.querySelectorAll<HTMLFieldSetElement>('fieldset'));
    this.options = fieldsets.map((fieldset) =>
      Array.from(fieldset.querySelectorAll<HTMLInputElement>('input')).find((r) => r.checked)!.value
    );
  }

  updateMedia(): void {
    if (!this.currentVariant?.featured_media) return;

    const mediaGallery = document.getElementById(
      `MediaGallery-${this.dataset.section}`
    ) as MediaGalleryElement | null;
    if (!mediaGallery || typeof mediaGallery.setActiveMedia !== 'function') return;

    mediaGallery.setActiveMedia(
      `${this.dataset.section}-${this.currentVariant.featured_media.id}`,
      true
    );

    const thumbnailSlider = mediaGallery.querySelector<HTMLElement>('.thumbnail-slider');
    if (thumbnailSlider) {
      const firstImg = thumbnailSlider.querySelector<HTMLImageElement>('li:first-of-type img');
      const sourceImg = mediaGallery.querySelector<HTMLImageElement>(
        '.product__media-list li:first-of-type .product__media img'
      );
      if (firstImg && sourceImg) { firstImg.srcset = ''; firstImg.src = sourceImg.src; }
    }

    const modalContent = document.querySelector(
      `#ProductModal-${this.dataset.section} .product-media-modal__content`
    );
    if (!modalContent) return;
    const modalFirstImg = modalContent.querySelector<HTMLImageElement>('img:first-of-type');
    if (modalFirstImg) {
      const sourceImg = mediaGallery.querySelector<HTMLImageElement>(
        '.product__media-list li:first-of-type .product__media img'
      );
      if (sourceImg) { modalFirstImg.srcset = ''; modalFirstImg.src = sourceImg.src; }
    }
  }

  updateInspiration(): void {
    if (!this.currentVariant) return;
    const currentOption = this.currentVariant.option1;
    const list = document.querySelector<HTMLElement>('.inspiration-blocks.--impact .inspiration-grid');
    if (!list) return;

    const optionMap: Record<string, string | undefined> = {
      'Full Size Photo':              list.dataset.full,
      'Frame Music Player':           list.dataset.frame,
      'Simple Music Player':          list.dataset.simple,
      'Album Music Player':           list.dataset.album,
      'Mosaic 1':                     list.dataset.mosaicUn,
      'Mosaic 2':                     list.dataset.mosaicDeux,
      'Square Photo':                 list.dataset.square,
      'Taped Photo':                  list.dataset.tapedPhoto,
      'Taped Polaroid':               list.dataset.tapedPolaroid,
      'Taped Polaroid on Mosaic':     list.dataset.tapedPolaroidMosaic,
    };
    const photoList = optionMap[currentOption] ?? list.dataset.full ?? '';

    let counter = 1;
    list.innerHTML = photoList
      .split('|')
      .filter((img) => img !== '')
      .map((img) => {
        const html = `<li>
          <picture>
            <source media="(min-width:521px)" srcset="//www.kasemedesign.ca/cdn/shop/${img}?crop=center&height=380&width=380 1x, //www.kasemedesign.ca/cdn/shop/${img}?crop=center&height=760&width=760 2x">
            <source media="(max-width:520px)" srcset="//www.kasemedesign.ca/cdn/shop/${img}?crop=center&height=200&width=200 1x, //www.kasemedesign.ca/cdn/shop/${img}?crop=center&height=400&width=400 2x">
            <img src="//www.kasemedesign.ca/cdn/shop/${img}?crop=center&height=200&width=200" alt="Inspiration ${counter}" width="380" height="380" loading="lazy">
          </picture>
        </li>`;
        counter++;
        return html;
      })
      .join('');
  }
}

customElements.define('variant-radios', VariantRadios);

// ---------------------------------------------------------------------------
// VariantHandler
// ---------------------------------------------------------------------------

class VariantHandler extends VariantSelects {
  variantOrder: string | undefined;
  upsellContainer: HTMLElement | null;

  constructor() {
    super();
    // Remove the raw 'change' listener inherited from VariantSelects — VariantHandler
    // receives updates exclusively via 'variant-changed' events bubbled from its
    // child variant-selects and variant-radios elements. Without this, every radio/select
    // change triggers onVariantChange twice, and the second call may fail to match a variant.
    this.removeEventListener('change', this.onVariantChange);
    this.addEventListener('variant-changed', this.onVariantChange);
    this.variantOrder = this.dataset.variantOrder;
    this.upsellContainer = document.querySelector<HTMLElement>('.upsell-product-container');

    this.querySelectorAll<HTMLSelectElement>('.product-form__input select').forEach((select) => {
      new Choices(select, { shouldSort: false, searchResultLimit: 100, itemSelectText: '', position: 'bottom' });
      keepChoicesDropdownVisible(select);
    });

    const dialogOpenButton = this.querySelector<HTMLElement>('[data-toggle-phone-model]');
    const dialogCloseButton = this.querySelector<HTMLElement>('.dialog [data-dialog-close]');
    const dialog = this.querySelector<HTMLDialogElement>('.dialog');

    if (dialogOpenButton && dialog) {
      dialogOpenButton.addEventListener('click', () => {
        document.body.classList.add('--no-scroll');
        dialog.showModal();
      });
      dialogCloseButton?.addEventListener('click', () => {
        document.body.classList.remove('--no-scroll');
        dialog.close();
      });
    }
  }

  onVariantChange(): void {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.updateMagsafeLegend();
      this.renderProductInfo();
      this.updateShareUrl();
      if (this.upsellContainer) this.updateUpsell();

      const price = this.currentVariant.price;
      const kmRewardMention = document.querySelector<HTMLElement>('.km-reward-mention');
      if (kmRewardMention) {
        let points = price / 100;
        const lastTwoDigits = String(price % 100).padStart(2, '0');
        if (lastTwoDigits !== '00') points = Number(points) + 1;
        points = Math.trunc(points) * 10;
        const em = kmRewardMention.querySelector('em');
        if (em) em.textContent = `${points} points`;
      }
    }
  }

  updateOptions(): void {
    const options: string[][] = [];
    const fieldsets = Array.from(this.querySelectorAll<HTMLFieldSetElement>('fieldset'));
    options.push(
      fieldsets.map((fs) =>
        Array.from(fs.querySelectorAll<HTMLInputElement>('input')).find((r) => r.checked)!.value
      )
    );
    options.push(
      Array.from(this.querySelectorAll<HTMLSelectElement>('select'), (s) => s.value)
    );
    this.options = this.variantOrder === 'mixed-reverse'
      ? options.reverse().flat()
      : options.flat();
  }

  // Only relevant for mixed-reverse picker where option1 is the device name.
  // The Magsafe Ring fieldset is only rendered in that picker type, so this
  // assumption holds for all current product configurations.
  updateMagsafeLegend(): void {
    if (!this.currentVariant) return;
    const magsafeFieldset = document.querySelector<HTMLElement>('[data-option-name="magsafe-ring"]');
    if (!magsafeFieldset) return;
    const legend = magsafeFieldset.querySelector<HTMLElement>('legend');
    if (!legend) return;
    const isPixel = this.currentVariant.option1?.toLowerCase().includes('pixel');
    legend.textContent = isPixel
      ? magsafeFieldset.dataset.legendPixel ?? ''
      : magsafeFieldset.dataset.legendDefault ?? '';
  }

  updateMasterId(): void {
    this.currentVariant = this.getVariantData().find((variant) =>
      !variant.options.map((option, index) => this.options[index] === option).includes(false)
    );

    // Fallback for mixed-reverse only (device is options[0]): if the exact combo doesn't
    // exist (e.g. new device has no "With" Magsafe Ring variant), pick the first available
    // variant with the same device. renderProductInfo will then correct secondary selectors.
    // Not applied to "mixed" layout where options[0] is a radio value, not the device.
    if (!this.currentVariant && this.variantOrder === 'mixed-reverse') {
      this.currentVariant = this.getVariantData().find((variant) =>
        variant.options[0] === this.options[0] && variant.available
      ) || this.getVariantData().find((variant) =>
        variant.options[0] === this.options[0]
      );
    }
  }

  getVariantData(): ShopifyVariant[] {
    if (!this.variantData) {
      const jsonEl = this.querySelector<HTMLScriptElement>('[type="application/json"]')
        || document.getElementById(`VariantData-${this.dataset.section}`);
      this.variantData = JSON.parse(jsonEl?.textContent ?? '[]');
    }
    return this.variantData!;
  }

  updateUpsell(): void {
    this.upsellContainer?.querySelectorAll<HTMLElement>('.upsell-product').forEach((element) => {
      const upsellOptions = element.querySelector<HTMLSelectElement>('[name="product-options"]');
      if (!upsellOptions) return;

      switch (element.getAttribute('data-type')) {
        case 'Screen Protector':
        case 'Vitre Protectrice': {
          let isMatching = false;
          upsellOptions.querySelectorAll<HTMLOptionElement>('option').forEach((option) => {
            const phoneModels = (option.getAttribute('data-phone-model') ?? '').split('|');
            const activeValue = phoneModels.filter((o) => o === this.options[0]);
            if (activeValue.length > 0) {
              isMatching = true;
              option.selected = true;
              upsellOptions.dispatchEvent(new Event('change'));
            }
          });
          const errorMessage = element.querySelector<HTMLElement>('.error-message');
          if (errorMessage) errorMessage.style.display = isMatching ? 'none' : 'block';
          break;
        }
        default:
          break;
      }
    });
  }
}

customElements.define('variant-handler', VariantHandler);

// ---------------------------------------------------------------------------
// VariantSelectsCaseTypeFilter
// ---------------------------------------------------------------------------

class VariantSelectsCaseTypeFilter extends VariantSelects {
  constructor() {
    super();
    this.listenToChanges();
    // Only initialize case-type options when a Device/Modèle select exists.
    // Products without that select (e.g. iPad Clear Case with "Model", gift cards
    // with "Amount") would otherwise have their second-option select cleared and
    // disabled by updateCaseTypeOptions([]).
    const initialOptions = this.getVariantOptions();
    if (initialOptions.length) this.updateCaseTypeOptions(initialOptions);
  }

  getVariantOptions(): CaseTypeOption[] {
    const phoneVariant = this.querySelector<HTMLSelectElement>(
      '[name="options[Device]"], [name="options[Modèle]"]'
    )?.value;
    if (!phoneVariant) return [];

    return this.getVariantData()
      .filter((v) => v.options[0] === phoneVariant)
      .reduce<CaseTypeOption[]>((acc, v) => {
        acc.push({ label: v.options[1], value: v.options[1] });
        return acc;
      }, []);
  }

  onPhoneVariantChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const watchedNames = [
      'options[Device]', 'options[Modèle]', 'options[Bumper Color]', 'options[Couleur]',
    ];
    if (!watchedNames.includes(target.name)) return;

    const options = this.getVariantOptions();
    if (!options.length) return;
    this.updateCaseTypeOptions(options);

    const colorVariant = document.querySelector<HTMLElement>('.related-products__list');
    const option1 = this.currentVariant?.option1;
    colorVariant?.querySelectorAll<HTMLAnchorElement>('.related-products__link').forEach((link) => {
      const variantId = option1 != null ? variantIdFor(link, option1) : undefined;
      if (variantId) {
        // split, not substring(indexOf): an href without ?variant= (AirGrip) must keep its path
        link.href = `${link.href.split('?')[0]}?variant=${variantId}`;
      }
    });
  }

  _swapKoboImages(): void {
    const imgToChange = document.querySelectorAll<HTMLElement>('[data-og-image]');
    if (this.querySelector('[name="options[Bumper Color]"], [name="options[Couleur]"]') || imgToChange.length === 0) return;
    const isKoboSelected = Array.from(this.querySelectorAll<HTMLSelectElement>('select')).some(
      (sel) => sel.value.toLowerCase().includes('kobo')
    );
    imgToChange.forEach((bloc) => {
      const img = bloc.querySelector<HTMLImageElement>('img');
      if (!img?.parentElement) return;
      const replacement = bloc.dataset.remplacementImg;
      const og = bloc.dataset.ogImage;
      if (!replacement || !og) return;
      img.parentElement.innerHTML = img.parentElement.innerHTML.replaceAll(
        isKoboSelected ? og : replacement,
        isKoboSelected ? replacement : og
      );
    });
  }

  listenToChanges(): void {
    this.addEventListener('change', this.onPhoneVariantChange.bind(this));
    this.addEventListener('change', () => this._swapKoboImages());

    this.querySelectorAll<HTMLElement>('[data-toggle-phone-model]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const dialog = btn.parentElement?.querySelector<HTMLDialogElement>('.dialog');
        if (!dialog) return;
        document.body.classList.add('--no-scroll');
        dialog.showModal();
      });
    });

    this.querySelectorAll<HTMLElement>('.dialog [data-dialog-close]').forEach((btn) => {
      btn.addEventListener('click', () => {
        document.body.classList.remove('--no-scroll');
        (btn.parentElement as HTMLDialogElement | null)?.close();
      });
    });
  }

  updateCaseTypeOptions(options: CaseTypeOption[], _initial?: boolean): void {
    const caseTypeSelect = this.querySelector<HTMLSelectElement>(
      '[name="options[Case Type]"], [name="options[Feature & Color]"], [name="options[Bumper Color]"], [name="options[Couleur]"]'
    );
    if (!caseTypeSelect) return;

    let selectedOption = '';
    while (caseTypeSelect.firstChild) {
      const child = caseTypeSelect.firstChild as HTMLOptionElement;
      if (child.selected && selectedOption === '') selectedOption = child.value;
      caseTypeSelect.removeChild(child);
    }

    options.forEach((option) => {
      const el = document.createElement('option');
      el.value = option.value;
      el.textContent = option.label;
      if (el.value === selectedOption) el.selected = true;
      caseTypeSelect.appendChild(el);
    });

    caseTypeSelect.disabled = options.length <= 1;

    const bumperColorSelect = this.querySelector<HTMLSelectElement>(
      '[name="options[Bumper Color]"], [name="options[Couleur]"]'
    );
    if (!bumperColorSelect) return;

    const imgBlocks = document.querySelectorAll<HTMLElement>('[data-og-image]');
    if (!imgBlocks.length) return;

    const isClear = bumperColorSelect.value.includes('Clear');
    imgBlocks.forEach((bloc) => {
      const img = bloc.querySelector<HTMLImageElement>('img');
      if (!img) return;
      const clear = bloc.dataset.remplacementImg ?? '';
      const og = bloc.dataset.ogImage ?? '';
      const parent = img.parentElement;
      if (!parent) return;
      parent.innerHTML = isClear
        ? parent.innerHTML.replaceAll(og, clear)
        : parent.innerHTML.replaceAll(clear, og);
    });

    const selects = this.querySelectorAll<HTMLSelectElement>('select');
    const currentVariants = this.getVariantData().filter(
      (v) => v.option1 === selects[0]?.value && v.option2 === selects[1]?.value
    );
    const price = currentVariants[0]?.price;
    if (!price) return;

    const kmRewardMention = document.querySelector<HTMLElement>('.km-reward-mention');
    if (kmRewardMention) {
      let points = price / 100;
      if (String(price % 100).padStart(2, '0') !== '00') points = Number(points) + 1;
      points = Math.trunc(points) * 10;
      const em = kmRewardMention.querySelector('em');
      if (em) em.innerHTML = `<strong>${points} points</strong>`;
    }
  }
}

customElements.define('variant-selects-case-type-filter', VariantSelectsCaseTypeFilter);
